import { FC, useEffect, useRef, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useTrialStatus } from '../../../hooks/use-trial-status';
import { useTrialWhatsNext } from '../../../queries/trial-whats-next';
import { TrialWhatsNextInterstitial } from './trial-whats-next-page';

/**
 * Login-time interstitial controller (RR-75). Mounted in the global app shell.
 *
 * Flow:
 * 1. Cheap client-side pre-filter (`useTrialStatus`) decides whether to even
 *    query — a finance/global admin whose company is in trial or recently
 *    lapsed (and not superadmin-initialized).
 * 2. The endpoint is authoritative: it 404s (-> `null`) when out of scope, and
 *    returns `shouldAutoPresent` for the real take-over decision.
 * 3. When `shouldAutoPresent` is true we render the full-screen interstitial.
 *    Pre-expiry it's dismissible; post-expiry it's more prominent. Dismissing
 *    or acting on a CTA writes the milestone dismissal (handled inside the
 *    interstitial) so it doesn't re-present.
 *
 * The take-over is also a real route (`/trial/whats-next`) rendered by
 * `TrialWhatsNextPage`; this component is only the auto-present path.
 */
export const TrialWhatsNextAutoPresent: FC = () => {
	const company = useCurrentCompany();
	const companyId = company?.id;
	const { isInScope, canSeeWhatsNext } = useTrialStatus();
	const eligible = isInScope && canSeeWhatsNext;
	const { data } = useTrialWhatsNext({ enabled: eligible });
	const { track } = useAnalytics();

	// "The user closed it" is keyed to the company id, not a plain boolean, so a
	// multi-company admin who dismisses for one company still sees the
	// interstitial after switching to another in-scope company in the same SPA
	// session (the query is already company-scoped). Cross-session suppression is
	// handled by the milestone dismissal, not this flag.
	const [closedForCompanyId, setClosedForCompanyId] = useState<string | undefined>(undefined);
	const hasClosed = closedForCompanyId !== undefined && closedForCompanyId === companyId;

	// `eligible` is included so a stale cached payload from a prior session can't
	// re-open the interstitial after the viewer no longer qualifies (e.g. company
	// converted to paid, role changed) before the query key re-scopes.
	const isOpen = eligible && !!data && data.shouldAutoPresent && !hasClosed;

	// `AutoPresented` is a side effect (network/analytics), so it lives in an
	// effect rather than render — fired once per company, guarded by a ref that
	// survives StrictMode's double-invoke and resets when the company changes.
	const trackedForCompanyIdRef = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!isOpen || trackedForCompanyIdRef.current === companyId) return;
		trackedForCompanyIdRef.current = companyId;
		track(AnalyticsEvent.TrialWhatsNext.AutoPresented, { phase: data.phase, milestone: data.milestone });
	}, [isOpen, companyId, data?.phase, data?.milestone, track]);

	if (!isOpen) return null;

	return <TrialWhatsNextInterstitial data={data} onClose={() => setClosedForCompanyId(companyId)} />;
};
